// ============================================================
// DOCUMENT/RECEIPT TYPES
// ============================================================

/**
 * OCR processing status for documents
 */
export type OcrStatus = 'not_started' | 'queued' | 'processing' | 'succeeded' | 'failed';

/**
 * Document type - receipt or expense invoice
 */
export type DocumentType = 'receipt' | 'expense_invoice';

/**
 * Source of the document upload
 */
export type DocumentSource = 'web' | 'mobile';

/**
 * Base document record from the documents table
 */
export interface Document {
  id: string;
  businessId: string;
  projectId: string | null;
  supplierId: string | null;
  uploadedBy: string;
  uploadedAt: string;
  filePath: string;
  mimeType: string | null;
  documentType: DocumentType;
  source: DocumentSource;
  vendorNameRaw: string | null;
  documentDate: string | null;
  currency: string | null;
  totalAmount: number | null;
  taxAmount: number | null;
  ocrStatus: OcrStatus;
  ocrLastAttemptAt: string | null;
  ocrErrorMessage: string | null;
  ocrProvider: string | null;
  createdAt: string;
  updatedAt: string;
}

/**
 * Document from v_documents view with all related entity joins
 */
export interface DocumentView extends Document {
  // Computed flags
  hasProject: boolean;
  hasSupplier: boolean;
  // Project context
  projectName: string | null;
  projectStatus: string | null;
  // Customer context (via project)
  customerFirstName: string | null;
  customerLastName: string | null;
  customerName: string | null;
  // Supplier context
  supplierName: string | null;
  // Uploader context
  uploadedByName: string | null;
  // Business context
  businessName: string | null;
}

/**
 * Unassigned receipt from v_unassigned_receipts view
 */
export interface UnassignedReceiptView {
  id: string;
  businessId: string;
  supplierId: string | null;
  uploadedBy: string;
  uploadedAt: string;
  filePath: string;
  mimeType: string | null;
  documentType: DocumentType;
  source: DocumentSource;
  vendorNameRaw: string | null;
  documentDate: string | null;
  currency: string | null;
  totalAmount: number | null;
  taxAmount: number | null;
  ocrStatus: OcrStatus;
  ocrErrorMessage: string | null;
  createdAt: string;
  // Joined fields
  supplierName: string | null;
  uploadedByName: string | null;
}

// ============================================================
// UPLOAD TYPES
// ============================================================

/**
 * Status of a receipt file during the upload process
 */
export type ReceiptUploadStatus = 
  | 'pending'    // Just added, not processed yet
  | 'ready'      // Ready to upload
  | 'uploading'  // Currently uploading
  | 'uploaded'   // Successfully uploaded
  | 'error';     // Upload failed

/**
 * A receipt file in the upload queue
 */
export interface ReceiptFile {
  /** Unique identifier for this upload instance */
  id: string;
  /** The actual File object */
  file: File;
  /** Preview URL for images, null for PDFs */
  preview: string | null;
  /** Whether this is a PDF file */
  isPdf: boolean;
  /** Current upload status */
  status: ReceiptUploadStatus;
  /** Error message if status is 'error' */
  error?: string;
  /** Upload progress percentage (0-100) */
  uploadProgress?: number;
  /** Optional: pre-selected project */
  selectedProjectId?: string | null;
  /** Optional: pre-selected supplier */
  selectedSupplierId?: string | null;
}

/**
 * Parameters for uploading a receipt
 */
export interface UploadReceiptParams {
  file: File;
  businessId: string;
  uploadedBy: string;
  projectId?: string | null;
  supplierId?: string | null;
  documentType?: DocumentType;
  source?: DocumentSource;
}

// ============================================================
// SUPPLIER TYPES
// ============================================================

/**
 * Supplier/vendor record from the suppliers table
 */
export interface Supplier {
  id: string;
  businessId: string;
  name: string;
  contactEmail: string | null;
  phone: string | null;
  defaultCurrency: string | null;
  createdAt: string;
  updatedAt: string;
}

/**
 * Simplified supplier option for dropdowns
 */
export interface SupplierOption {
  id: string;
  name: string;
}

/**
 * Input for creating a new supplier (minimal - just name)
 */
export interface CreateSupplierInput {
  businessId: string;
  name: string;
}

// ============================================================
// MAPPING HELPERS
// ============================================================

/**
 * Map database row to Document type
 */
export function mapDocumentFromDb(row: Record<string, unknown>): Document {
  return {
    id: row.id as string,
    businessId: row.business_id as string,
    projectId: row.project_id as string | null,
    supplierId: row.supplier_id as string | null,
    uploadedBy: row.uploaded_by as string,
    uploadedAt: row.uploaded_at as string,
    filePath: row.file_path as string,
    mimeType: row.mime_type as string | null,
    documentType: row.document_type as DocumentType,
    source: row.source as DocumentSource,
    vendorNameRaw: row.vendor_name_raw as string | null,
    documentDate: row.document_date as string | null,
    currency: row.currency as string | null,
    totalAmount: row.total_amount as number | null,
    taxAmount: row.tax_amount as number | null,
    ocrStatus: row.ocr_status as OcrStatus,
    ocrLastAttemptAt: row.ocr_last_attempt_at as string | null,
    ocrErrorMessage: row.ocr_error_message as string | null,
    ocrProvider: row.ocr_provider as string | null,
    createdAt: row.created_at as string,
    updatedAt: row.updated_at as string,
  };
}

/**
 * Map database row to DocumentView type
 */
export function mapDocumentViewFromDb(row: Record<string, unknown>): DocumentView {
  return {
    ...mapDocumentFromDb(row),
    hasProject: row.has_project as boolean,
    hasSupplier: row.has_supplier as boolean,
    projectName: row.project_name as string | null,
    projectStatus: row.project_status as string | null,
    customerFirstName: row.customer_first_name as string | null,
    customerLastName: row.customer_last_name as string | null,
    customerName: row.customer_name as string | null,
    supplierName: row.supplier_name as string | null,
    uploadedByName: row.uploaded_by_name as string | null,
    businessName: row.business_name as string | null,
  };
}

/**
 * Map database row to UnassignedReceiptView type
 */
export function mapUnassignedReceiptFromDb(row: Record<string, unknown>): UnassignedReceiptView {
  return {
    id: row.id as string,
    businessId: row.business_id as string,
    supplierId: row.supplier_id as string | null,
    uploadedBy: row.uploaded_by as string,
    uploadedAt: row.uploaded_at as string,
    filePath: row.file_path as string,
    mimeType: row.mime_type as string | null,
    documentType: row.document_type as DocumentType,
    source: row.source as DocumentSource,
    vendorNameRaw: row.vendor_name_raw as string | null,
    documentDate: row.document_date as string | null,
    currency: row.currency as string | null,
    totalAmount: row.total_amount as number | null,
    taxAmount: row.tax_amount as number | null,
    ocrStatus: row.ocr_status as OcrStatus,
    ocrErrorMessage: row.ocr_error_message as string | null,
    createdAt: row.created_at as string,
    supplierName: row.supplier_name as string | null,
    uploadedByName: row.uploaded_by_name as string | null,
  };
}

/**
 * Map database row to Supplier type
 */
export function mapSupplierFromDb(row: Record<string, unknown>): Supplier {
  return {
    id: row.id as string,
    businessId: row.business_id as string,
    name: row.name as string,
    contactEmail: row.contact_email as string | null,
    phone: row.phone as string | null,
    defaultCurrency: row.default_currency as string | null,
    createdAt: row.created_at as string,
    updatedAt: row.updated_at as string,
  };
}

// ============================================================
// UTILITY TYPES
// ============================================================

/**
 * Supported file types for receipt upload
 */
export const RECEIPT_ACCEPTED_TYPES = [
  'image/jpeg',
  'image/png',
  'image/heic',
  'image/heif',
  'image/webp',
  'application/pdf',
] as const;

/**
 * Check if a file is a supported receipt type
 */
export function isValidReceiptType(file: File): boolean {
  return RECEIPT_ACCEPTED_TYPES.includes(file.type as typeof RECEIPT_ACCEPTED_TYPES[number]);
}

/**
 * Check if a file is a PDF
 */
export function isPdfFile(file: File): boolean {
  return file.type === 'application/pdf';
}

/**
 * Generate a unique ID for a receipt file in the upload queue
 */
export function generateReceiptId(): string {
  return `receipt_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
}

/**
 * OCR status display configuration
 */
export const OCR_STATUS_CONFIG: Record<OcrStatus, { label: string; color: string }> = {
  not_started: { label: 'Pending OCR', color: 'bg-secondary-100 text-secondary-600' },
  queued: { label: 'Queued', color: 'bg-blue-100 text-blue-600' },
  processing: { label: 'Processing', color: 'bg-yellow-100 text-yellow-600' },
  succeeded: { label: 'Processed', color: 'bg-green-100 text-green-600' },
  failed: { label: 'OCR Failed', color: 'bg-red-100 text-red-600' },
};

/**
 * Document type display configuration
 */
export const DOCUMENT_TYPE_CONFIG: Record<DocumentType, { label: string }> = {
  receipt: { label: 'Receipt' },
  expense_invoice: { label: 'Expense Invoice' },
};